import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import ReactDOM from 'react-dom'
import cx from 'classnames'
import SimpleTooltips from './SimpleTooltips'
import ReactContextForwarder from '../utils/ReactContextForwarder'
import { DndProvider } from 'react-dnd'
import HTML5Backend from 'react-dnd-html5-backend'
import TouchBackend from 'react-dnd-touch-backend'
import {stepFocus} from '../utils.js'

/**
 * Wrapper component that renders its children into a child of document.body; this allows
 * modal detail dialogs to be rendered and managed like a simple sub-component but not be
 * clipped by the bounds of the parent element.
 */
class DialogContainer extends React.Component {
  static displayName = 'DialogContainer'

  static propTypes = {
    /**
     * Called at render time to determine the size/position of the dialog content, relative to
     * the viewport. If not supplied then the dialog's content will need to have its size/position
     * set via CSS.
     */
    getRect: T.func,
    modal: T.bool, // If true, the viewport will be masked with an unclickable overlay.
    onMaskClick: T.func, // Called when the bg blur/container is clicked
    children: T.node
  }

  static contextTypes = {
    dragDropManager: T.object,
    onModalDialogOpened: T.func,
    onModalDialogClosed: T.func,
    store: T.any
  }

  constructor(props) {
    super(props)
  }

  componentDidMount() {
    this.actuallyRender()
    if (this.context.onModalDialogOpened && this.props.modal === true) {
      this.context.onModalDialogOpened()
    }
  }

  componentDidUpdate() {
    this.actuallyRender()
  }

  componentWillUnmount() {
    const node = this._mountNode
    if (node) {
      ReactDOM.unmountComponentAtNode(node)
      if (node.parentNode) {
        node.parentNode.removeChild(node)
      }
      delete this._mountNode
    }
    if (this.context.onModalDialogClosed && this.props.modal === true) {
      this.context.onModalDialogClosed()
    }
  }

  handleMaskClick = e => {
    if (
      this.props.onMaskClick &&
      (e.target.getAttribute && e.target.getAttribute('class') || '').indexOf('dialog_ct_content') > -1
    ) {
      this.props.onMaskClick()
    }
  }

  _onDomRef = node => {
    // If this is a modal dialog, bring focus into it automatically
    // TODO should we do this for non-modals too? Or add an option for it?
    if (node && this.props.modal) {
      stepFocus(node, 1)
    }
  }

  actuallyRender = () => {
    const { getRect, modal, children } = this.props
    let mountNode = this._mountNode
    if (!mountNode) {
      mountNode = this._mountNode = document.createElement('div')
      document.body.appendChild(mountNode)
    }
    mountNode.className = cx('dialog_ct_mount_node', modal && 'is_modal')

    const rect = getRect && getRect()
    const rectStyles = rect && _.pick(rect, 'top', 'left', 'width', 'height')

    ReactDOM.render(
      <ReactContextForwarder context={_.clone(this.context)}>
        <DndProvider
          backend={window._pwCompatibility.isTouchscreen ? TouchBackend : HTML5Backend}
          options={{ enableMouseEvents: true }}
        >
          <SimpleTooltips>
            <div
              className={`dialog_ct_content`}
              onClick={this.handleMaskClick}
              style={rectStyles}
              role="dialog"
              aria-modal={modal}
              ref={this._onDomRef}
            >
              {React.Children.only(children)}
            </div>
          </SimpleTooltips>
        </DndProvider>
      </ReactContextForwarder>,
      mountNode
    )
  }

  render() {
    return null
  }
}

export default DialogContainer
